import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CloudApiService } from './cloud-api.service';
import { environment } from '../../../environments/environment';
import { IFileExplorerService } from 'src/api/file-explorer.service.interface';
import {
    FileExplorerListResponse,
    FileExplorerFileInfo,
    FileExplorerReadResponse
} from 'src/api/api.service';

/**
 * Cloud mode adapter for file explorer.
 * Routes all file operations through the DeviceRegistry.Api REST endpoints
 * which relay to the device via the WebSocket tunnel.
 *
 * Instantiated per-component with a deviceId set via setDeviceId().
 */
@Injectable()
export class CloudFileExplorerService implements IFileExplorerService {
    private http = inject(HttpClient);
    private cloudApi = inject(CloudApiService);

    private _deviceId: string = '';

    setDeviceId(deviceId: string): void {
        this._deviceId = deviceId;
    }

    private get apiBase(): string {
        return `${environment.apiBaseUrl}/devices/${this._deviceId}`;
    }

    private authHeaders(): HttpHeaders {
        const token = this.cloudApi.token();
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        if (token) headers = headers.set('Authorization', `Bearer ${token}`);
        return headers;
    }

    async fileExplorerListDir(path: string = '/'): Promise<FileExplorerListResponse> {
        const params = new HttpParams().set('path', path);
        const response = await firstValueFrom(
            this.http.get<any>(`${this.apiBase}/files`, { headers: this.authHeaders(), params })
        );
        console.log('[CloudFileExplorer] listDir response:', JSON.stringify(response, null, 2));
        console.log('[CloudFileExplorer] response.success:', response?.success);
        console.log('[CloudFileExplorer] response.entries:', response?.entries?.length);
        return response;
    }

    async fileExplorerReadFile(path: string, maxBytes?: number): Promise<FileExplorerReadResponse> {
        let params = new HttpParams().set('path', path);
        if (maxBytes != null) params = params.set('maxBytes', maxBytes);
        return firstValueFrom(
            this.http.get<any>(`${this.apiBase}/files/read`, { headers: this.authHeaders(), params })
        );
    }

    async fileExplorerGetFileInfo(path: string): Promise<FileExplorerFileInfo> {
        const params = new HttpParams().set('path', path);
        return firstValueFrom(
            this.http.get<any>(`${this.apiBase}/files/info`, { headers: this.authHeaders(), params })
        );
    }

    async fileExplorerDownloadFile(path: string): Promise<FileExplorerReadResponse> {
        const params = new HttpParams().set('path', path);
        return firstValueFrom(
            this.http.get<any>(`${this.apiBase}/files/download`, { headers: this.authHeaders(), params })
        );
    }

    async fileExplorerDeleteFile(path: string): Promise<{ success: boolean; error?: string; path?: string; message?: string }> {
        const params = new HttpParams().set('path', path);
        return firstValueFrom(
            this.http.delete<any>(`${this.apiBase}/files`, { headers: this.authHeaders(), params })
        );
    }

    async fileExplorerSaveFile(path: string, content: string): Promise<{ success: boolean; error?: string; path?: string; name?: string; sizeBytes?: number; modifiedUtc?: string; message?: string }> {
        return firstValueFrom(
            this.http.put<any>(`${this.apiBase}/files`, { path, content }, { headers: this.authHeaders() })
        );
    }

    async fileExplorerUploadFile(folderPath: string, file: File): Promise<{ success: boolean; error?: string; path?: string; name?: string; sizeBytes?: number; extension?: string; message?: string }> {
        const contentBase64 = await this.fileToBase64(file);
        return firstValueFrom(
            this.http.post<any>(`${this.apiBase}/files/upload`, {
                folderPath,
                fileName: file.name,
                contentBase64
            }, { headers: this.authHeaders() })
        );
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                // Strip the data URL prefix (e.g. "data:application/octet-stream;base64,")
                const base64 = result.split(',')[1] ?? '';
                resolve(base64);
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }
}
